import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { ArrowUp } from "react-feather";
import {
  Button,
  JsonEditor,
  PageTitle,
  PageWrapperTwoColumn,
  ReadmePanel,
  Section,
  Select,
  Spinner,
  Tabs,
} from "@cinesys/ui/components";
import ApiService from "../../services/ApiService";
import { useOrgSettingsDocument } from "./useOrgSettingsDocument";

interface SettingsSurfaceListEntry {
  plugin: string;
  title: string;
  hasMainConfig: boolean;
  hasReadMe: boolean;
  files: string[];
}

const StyledSelect = styled(Select)`
  max-width: 30vw;
` as typeof Select;

const ChooseSurfaceBanner = styled.div`
  background-color: var(--blueBackground);
  color: var(--gray400);
  height: 30px;
  padding: 0 16px;
  margin: 0;
  display: flex;
  align-items: center;
  text-align: left;
  gap: 5px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
`;

/**
 * Dashboard tab for this org's settings, across every registered `ORG_SETTING_SURFACES` entry
 * (backend). The picker frame — a `Select` in the `Section`'s title slot, plus a nested `Tabs`
 * row for per-surface files — copies `cs_config_editor`'s own picker shape (cantemo-plugins)
 * directly, using local component state instead of that app's route-param model: this screen has
 * no need for URL-bookmarkable surface/file selection. Every surface here is single-document, so
 * the file tabs only ever render one synthetic "Config" entry — built generically off
 * `hasMainConfig`/`files` rather than hardcoded to that, so a future multi-file surface needs no
 * changes here.
 */
const OrgSettingsScreen = () => {
  const { systemDomainId } = useParams<{ systemDomainId: string }>();
  const [surface, setSurface] = useState<string | undefined>();
  const [file, setFile] = useState("main");

  const {
    data: listData,
    isLoading: listLoading,
    isError: listError,
  } = useQuery({
    queryKey: ["orgSettings", "all_settings", systemDomainId],
    enabled: !!systemDomainId,
    queryFn: async () =>
      ApiService.fetch<{ configs: SettingsSurfaceListEntry[] }>(
        "GET",
        `/api/config/organization/${systemDomainId}/all_settings`
      ),
  });
  const configs = listData?.configs ?? [];
  const selected = configs.find((c) => c.plugin === surface);

  const {
    data,
    isLoading,
    isError,
    save,
    isSaving,
    saveError,
    clear,
    isClearing,
    clearError,
  } = useOrgSettingsDocument(systemDomainId, surface);

  const [editorValue, setEditorValue] = useState("");
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    if (data) {
      setEditorValue(data.content);
    }
  }, [data]);

  const dirty = !!data && editorValue !== data.content;

  const tabs = [
    ...(selected?.hasMainConfig ? [{ key: "main", label: "Config" }] : []),
    ...(selected?.files.map((f) => ({ key: f, label: f })) ?? []),
  ];

  return (
    <div>
      <PageTitle
        title="Settings"
        error={
          listError
            ? "Problem loading settings surfaces"
            : isError
            ? "Problem loading settings"
            : saveError || clearError
        }
      />
      {listLoading ? (
        <Spinner />
      ) : (
        !listError && (
          <Section
            noMargin
            title={
              <StyledSelect
                width={450}
                maxMenuHeight={600}
                options={configs.map((c) => ({
                  label: c.title,
                  value: c.plugin,
                }))}
                value={surface}
                onChange={(option) => {
                  setSurface((option as { value: string }).value);
                  setFile("main");
                }}
                placeholder="Select a surface..."
                portalTarget={document.getElementById("PortalTarget")}
                onClear={() => setSurface(undefined)}
              />
            }
            buttons={
              surface && (
                <Buttons>
                  <Button
                    type="success"
                    label="Save"
                    onClick={() => save(editorValue)}
                    disabled={isInvalid || !dirty}
                    loading={isSaving}
                  />
                  <Button
                    type="danger"
                    label="Clear override"
                    onClick={() => {
                      if (
                        window.confirm(
                          `This clears every one of this org's overrides for ${surface}, falling back to the global default. Continue?`
                        )
                      ) {
                        clear();
                      }
                    }}
                    loading={isClearing}
                  />
                </Buttons>
              )
            }
          >
            {!surface ? (
              <ChooseSurfaceBanner>
                <ArrowUp size={17} />
                Choose a surface from the dropdown to configure
                <ArrowUp size={17} />
              </ChooseSurfaceBanner>
            ) : (
              <>
                {tabs.length > 0 && (
                  <Tabs tabs={tabs} value={file} onChange={setFile} />
                )}
                {isLoading ? (
                  <Spinner />
                ) : (
                  !isError && (
                    <PageWrapperTwoColumn id="org_settings" padding="0">
                      <div>
                        <JsonEditor
                          height="calc(100vh - 260px)"
                          language="json"
                          value={editorValue}
                          onChange={(value) => setEditorValue(value ?? "")}
                          setIsInvalid={setIsInvalid}
                        />
                      </div>
                      <ReadmePanel content={data?.readme ?? ""} />
                    </PageWrapperTwoColumn>
                  )
                )}
              </>
            )}
          </Section>
        )
      )}
    </div>
  );
};

export default OrgSettingsScreen;
